import { useCallback } from 'react';
import { ToastProps as CoreUiToastProps } from '@hopskipdrive/react-components';
import { useAnnouncer } from '@rideiq/shared/contexts/LiveRegionAnnouncerProvider';
import { create } from 'zustand';

export type ToastProps = CoreUiToastProps & {
  sticky?: boolean;
};

export type InternalToast = ToastProps & { toastId: number };

export const AUTO_HIDE_DURATION_BY_SEVERITY: Record<ToastProps['severity'], number> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: 10000,
};

interface ToastStoreState {
  toasts: InternalToast[];
  addToast: (toast: ToastProps) => void;
  removeToast: (toastId: number) => void;
  resetToasts: (clearStickyToasts?: boolean) => void;
}

export const useToastStoreState = create<ToastStoreState>((set) => ({
  toasts: [],
  addToast: (toast) =>
    set((state) => ({
      toasts: [...state.toasts, { ...toast, toastId: Date.now() + Math.random() }],
    })),
  removeToast: (toastId) =>
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.toastId !== toastId),
    })),
  resetToasts: (clearStickyToasts = false) =>
    set((state) => ({
      toasts: clearStickyToasts ? [] : state.toasts.filter((toast) => toast.sticky),
    })),
}));

export function useToastStore(): ToastStoreState {
  const { announce } = useAnnouncer();
  const toasts = useToastStoreState((state) => state.toasts);
  const removeToast = useToastStoreState((state) => state.removeToast);
  const resetToasts = useToastStoreState((state) => state.resetToasts);
  const storeAddToast = useToastStoreState((state) => state.addToast);

  const addToast = useCallback(
    (toast: ToastProps) => {
      storeAddToast(toast);
      if (typeof toast.message === 'string') {
        announce(toast.message);
      }
    },
    [storeAddToast, announce]
  );

  return { toasts, addToast, removeToast, resetToasts };
}
